<template>
  <div class="flex justify-center h-full space-y-2.5 my-2.5 py-8">
    <div
      v-if="project"
      class="bg-white rounded-xl shadow-sm border border-gray-200 w-[90%]"
    >
      <!-- Header Section -->
      <div class="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 rounded-t-xl">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">
              {{ project.name }} {{ $t('ProjectSettings.title') }}
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              Manage project configuration and settings
            </p>
          </div>

          <div class="flex items-center space-x-3">
            <button
              v-if="userStore.isRole(['company_admin', 'project_admin']) && project.archived_at"
              type="button"
              class="btn btn--tertiary-blue flex items-center space-x-2"
              @click="unarchiveProject"
            >
              <span>{{ $t('ProjectSettings.unarchive') }}</span>
            </button>

            <button
              v-else-if="userStore.isRole(['company_admin', 'project_admin'])"
              type="button"
              class="btn btn--tertiary-blue flex items-center space-x-2"
              @click="archiveProject"
            >
              <span>{{ $t('ProjectSettings.archive') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Tab Navigation -->
      <div class="px-4 py-2 border-b border-gray-200 bg-white">
        <nav class="flex space-x-8 overflow-x-auto">
          <button
            v-if="userStore.isRole(['admin', 'company_admin']) && project?.company_id"
            type="button"
            class="py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200"
            :class="selectedTab === 'company'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            data-test="company-tab"
            @click="selectTab('company')"
          >
            {{ $t('ProjectSettings.company') }}
          </button>

          <button
            v-if="userStore.isRole(['admin', 'company_admin']) && project?.company_id"
            type="button"
            class="py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200"
            :class="selectedTab === 'company_users'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            data-test="company_users-tab"
            @click="selectTab('company_users')"
          >
            {{ $t('ProjectSettings.companyUsers') }}
          </button>

          <button
            v-if="userStore.isRole(['admin', 'company_admin', 'project_admin'])"
            type="button"
            class="py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200"
            :class="selectedTab === 'project'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            data-test="project-tab"
            @click="selectTab('project')"
          >
            {{ $t('ProjectSettings.project') }}
          </button>

          <button
            v-if="userStore.isRole(['admin', 'company_admin', 'project_admin'])"
            type="button"
            class="py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200"
            :class="selectedTab === 'clients'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            data-test="clients-tab"
            @click="selectTab('clients')"
          >
            {{ $t('ProjectSettings.projectClients') }}
          </button>

          <button
            v-if="userStore.isRole(['admin', 'company_admin', 'project_admin', 'contract_admin', 'contract_manager'])"
            type="button"
            class="py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200"
            :class="selectedTab === 'head_contract'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            data-test="head-contract-tab"
            @click="selectTab('head_contract')"
          >
            {{ $t('ProjectSettings.headContract') }}
          </button>

          <button
            v-if="userStore.isRole(['admin', 'company_admin', 'project_admin', 'contract_admin', 'contract_manager'])"
            type="button"
            class="py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200"
            :class="selectedTab === 'subcontract'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            @click="selectTab('subcontract')"
          >
            Subcontract
          </button>

          <button
            v-if="userStore.isRole(['admin', 'company_admin', 'project_admin', 'engineer', 'site_manager']) && project?.isContractor"
            type="button"
            class="py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200"
            :class="selectedTab === 'self_perform'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            data-test="self-perform-tab"
            @click="selectTab('self_perform')"
          >
            {{ $t('ProjectSettings.selfPerform') }}
          </button>

          <button
            v-if="userStore.isRole(['admin', 'company_admin', 'project_admin', 'engineer', 'site_manager'])"
            type="button"
            class="py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200"
            :class="selectedTab === 'vendors'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            @click="selectTab('vendors')"
          >
            {{ $t('ProjectSettings.projectVendors') }}
          </button>

          <button
            type="button"
            class="py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200"
            :class="selectedTab === 'user_settings'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            @click="selectTab('user_settings')"
          >
            {{ $t('ProjectSettings.userSettings') }}
          </button>
        </nav>
      </div>

      <!-- Tab Content -->
      <div class="p-6">
        <CompanyTab
          v-if="selectedTab === 'company'"
          :company-id="project?.company_id"
          @dirty="tabIsDirty = true"
          @saved="tabIsDirty = false"
        />

        <CompanyUsersTab
          v-if="selectedTab === 'company_users'"
          :company-id="project?.company_id"
          @dirty="tabIsDirty = true"
          @saved="tabIsDirty = false"
        />

        <ProjectTab
          v-else-if="selectedTab === 'project'"
          :project-id="$props.projectId"
          @dirty="tabIsDirty = true"
          @saved="tabIsDirty = false"
        />

        <VendorsTab
          v-else-if="selectedTab === 'vendors'"
          :project-id="$props.projectId"
          @dirty="tabIsDirty = true"
          @saved="tabIsDirty = false"
        />

        <SelfPerformTab
          v-else-if="selectedTab === 'self_perform'"
          :project-id="$props.projectId"
          @dirty="tabIsDirty = true"
          @saved="tabIsDirty = false"
        />

        <HeadContractTab
          v-else-if="selectedTab === 'head_contract'"
          :project-id="$props.projectId"
          @dirty="tabIsDirty = true"
          @saved="tabIsDirty = false"
        />

        <SubcontractTab
          v-else-if="selectedTab === 'subcontract'"
          :project-id="$props.projectId"
          @dirty="tabIsDirty = true"
          @saved="tabIsDirty = false"
        />

        <UserTab
          v-else-if="selectedTab === 'user_settings'"
          @dirty="tabIsDirty = true"
          @saved="tabIsDirty = false"
        />

        <ClientsTab
          v-else-if="selectedTab === 'clients'"
          @dirty="tabIsDirty = true"
          @saved="tabIsDirty = false"
        />
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-else
      class="min-h-screen bg-gray-100 flex items-center justify-center w-4/5 rounded-xl"
    >
      <div class="text-center">
        <AppSpinner class="h-8 w-8 mx-auto mb-4" />

        <p class="text-gray-600">
          Loading project settings...
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ClientsTab from '@/components/ProjectSettings/ClientsTab/ClientsTab.vue';
import CompanyTab from '@/components/ProjectSettings/CompanyTab/CompanyTab.vue';
import CompanyUsersTab from '@/components/ProjectSettings/CompanyUsersTab/CompanyUsersTab.vue';
import HeadContractTab from '@/components/ProjectSettings/HeadContractTab/HeadContractTab.vue';
import ProjectTab from '@/components/ProjectSettings/ProjectTab/ProjectTab.vue';
import SelfPerformTab from '@/components/ProjectSettings/SelfPerformTab/SelfPerformTab.vue';
import SubcontractTab from '@/components/ProjectSettings/SubcontractTab/SubcontractTab.vue';
import UserTab from '@/components/ProjectSettings/UserTab/UserTab.vue';
import VendorsTab from '@/components/ProjectSettings/VendorsTab/VendorsTab.vue';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { useWbsCodesStore } from '@/store/wbsCodes';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';

const props = defineProps<{
  projectId: string;
}>();

const projectsStore = useProjectsStore();
const userStore = useUserStore();
const wbsCodesStore = useWbsCodesStore();

const project = computed(() => {
  return projectsStore.models.find(parseInt(props.projectId));
});

watch(() => props.projectId, (newValue) => {
  if(newValue) {
    wbsCodesStore.index(newValue);
  }
}, { immediate: true });

const route = useRoute();
const router = useRouter();

const tabs = ref(
  [
    'company',
    'project',
    'self_perform',
    'head_contract',
    'company_users',
    'user_settings',
    'vendors',
    'clients',
    'subcontract',
  ] as const,
);

const selectedTab = ref<typeof tabs.value[number]>(undefined);
const tabIsDirty = ref(false);

// Function to check if user has access to a specific tab
const canAccessTab = (tab: string): boolean => {
  switch (tab) {
    case 'company':
      return userStore.isRole(['admin', 'company_admin']) && !!project.value?.company_id;

    case 'company_users':
      return userStore.isRole(['admin', 'company_admin']) && !!project.value?.company_id;

    case 'project':
      return userStore.isRole(['admin', 'company_admin', 'project_admin']);

    case 'clients':
      return userStore.isRole(['admin', 'company_admin', 'project_admin']);

    case 'head_contract':
      return userStore.isRole(['admin', 'company_admin', 'project_admin', 'contract_admin', 'contract_manager']);

    case 'subcontract':
      return userStore.isRole(['admin', 'company_admin', 'project_admin', 'contract_admin', 'contract_manager']);

    case 'self_perform':
      return userStore.isRole(['admin', 'company_admin', 'project_admin', 'engineer', 'site_manager']) &&
        project.value?.isContractor;

    case 'vendors':
      return userStore.isRole(['admin', 'company_admin', 'project_admin', 'engineer', 'site_manager']);

    case 'user_settings':
      return true; // Everyone has access to user settings

    default:
      return false;
  }
};

const selectTab = (tab: typeof tabs.value[number]) => {
  if(tabIsDirty.value) {
    Swal.fire({
      icon: 'warning',
      title: t('ProjectSettings.saveChanges'),
      text: t('ProjectSettings.unsavedChangesWarning'),
      showCancelButton: true,
      showConfirmButton: true,
    }).then((result) => {
      if(!result.isDismissed) {
        selectedTab.value = tab;
      }
    });
  } else {
    selectedTab.value = tab;
  }
};

const currentProjectRole = computed(() => {
  return userStore.currentProjectRole;
});

watch([() => props.projectId, currentProjectRole], (newValue) => {
  if(newValue) {
    // Check if there's a hash-based tab selection and user has access to it
    const hashTab = route.hash ? route.hash.slice(1) : undefined;
    const hasAccessToHashTab = hashTab && canAccessTab(hashTab);

    if(hasAccessToHashTab) {
      // Use hash-based selection if user has access
      selectedTab.value = hashTab as typeof tabs.value[number];
    } else {
      // Fall back to role-based defaults
      if(userStore.isRole(['admin', 'company_admin', 'project_admin'])) {
        selectTab('project');
      } else if(userStore.isRole(['engineer', 'site_manager'])) {
        selectTab('self_perform');
      } else if(userStore.isRole(['contract_admin', 'contract_manager'])) {
        selectTab('head_contract');
      } else {
        selectTab('user_settings');
      }
    }
  }
}, { immediate: true });

// Watch for route hash changes to handle direct navigation
watch(() => route.hash, (newHash) => {
  if(newHash) {
    const hashTab = newHash.slice(1);

    if(canAccessTab(hashTab)) {
      selectedTab.value = hashTab as typeof tabs.value[number];
    }
  }
}, { immediate: true });

watch(selectedTab, (newValue) => {
  if(newValue) {
    router.replace({ hash: `#${newValue}` });
    tabIsDirty.value = false;
  }
}, { immediate: true });

onBeforeRouteLeave(async (_to, _from) => {
  if(tabIsDirty.value) {
    const result = await Swal.fire({
      icon: 'warning',
      title: t('ProjectSettings.saveChanges'),
      text: t('ProjectSettings.unsavedChangesWarning'),
      showCancelButton: true,
      showConfirmButton: true,
    });

    if(result.isDismissed) {
      return false;
    }
  }
});

const deleteProjectAction = useStoreApiAction(projectsStore.deleteProject);
const archiveProjectAction = useStoreApiAction(projectsStore.archive);
const unarchiveProjectAction = useStoreApiAction(projectsStore.unarchive);

const deleteProject = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ProjectSettings.deleteProjectTitle', { name: project.value.name }),
    text: t('ProjectSettings.deleteProjectText'),
    input: 'text',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed && result.value === 'delete') {
      deleteProjectAction.request(props.projectId).then(() => {
        router.push({ name: 'ProjectSelector' });

        Swal.fire({
          icon: 'success',
          title: t('ProjectSettings.projectDeleted'),
        }).then(() => {
          window.location.reload();
        });
      });
    } else if(result.isConfirmed) {
      Swal.fire({
        icon: 'warning',
        title: t('ProjectSettings.inputNotMatched'),
        text: t('ProjectSettings.projectNotDeleted'),
      });
    }
  });
};

const archiveProject = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ProjectSettings.archiveProjectTitle', { name: project.value.name }),
    text: t('ProjectSettings.archiveProjectText'),
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      archiveProjectAction.request(props.projectId).then(() => {
        Swal.fire({
          icon: 'success',
          title: t('ProjectSettings.projectArchived'),
        });
      }).catch((error) => {
        if(error?.status === 403) {
          Swal.fire({
            icon: 'error',
            title: t('ProjectSettings.failedToUnarchive'),
            text: error?.data.message,
          });
        }
      });
    }
  });
};

const unarchiveProject = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ProjectSettings.unarchiveProjectTitle', { name: project.value.name }),
    text: t('ProjectSettings.unarchiveProjectText'),
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      unarchiveProjectAction.request(props.projectId).then(() => {
        Swal.fire({
          icon: 'success',
          title: t('ProjectSettings.projectUnarchived'),
        });
      }).catch((error) => {
        if(error?.status === 403) {
          Swal.fire({
            icon: 'error',
            title: t('ProjectSettings.failedToUnarchive'),
            text: error?.data.message,
          });
        }
      });
    }
  });
};
</script>

<style scoped></style>
